import React from 'react';
import { Facebook, Linkedin, Twitter, MessageCircle, Mail, Link2, Check } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Lightweight share-bar — opens the official share-intent of each network.
 * Use anywhere on the site:  <SocialShare url="…" title="…" message="…" />
 */
export const SocialShare = ({
  url,
  title = 'Löwenherz Invest AG',
  message = 'Werden Sie Aktionär unserer Schweizer Rendite-Immobilien — stabile Sachwerte, attraktive Dividenden.',
  className = '',
  size = 'md',
}) => {
  const shareUrl = url || (typeof window !== 'undefined' ? window.location.href : '');
  const enc = encodeURIComponent;
  const text = `${message} ${shareUrl}`;
  const [copied, setCopied] = React.useState(false);

  const links = [
    {
      key: 'facebook',
      label: 'Facebook',
      color: '#1877F2',
      icon: Facebook,
      href: `https://www.facebook.com/sharer/sharer.php?u=${enc(shareUrl)}&quote=${enc(message)}`,
    },
    {
      key: 'whatsapp',
      label: 'WhatsApp',
      color: '#25D366',
      icon: MessageCircle,
      href: `https://api.whatsapp.com/send?text=${enc(text)}`,
    },
    {
      key: 'linkedin',
      label: 'LinkedIn',
      color: '#0A66C2',
      icon: Linkedin,
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${enc(shareUrl)}`,
    },
    {
      key: 'twitter',
      label: 'X / Twitter',
      color: '#000000',
      icon: Twitter,
      href: `https://twitter.com/intent/tweet?text=${enc(message)}&url=${enc(shareUrl)}`,
    },
    {
      key: 'email',
      label: 'E-Mail',
      color: '#C4A55A',
      icon: Mail,
      href: `mailto:?subject=${enc(title)}&body=${enc(text)}`,
    },
  ];

  const dims = size === 'sm' ? 'h-8 w-8' : 'h-10 w-10';
  const iconDims = size === 'sm' ? 'h-3.5 w-3.5' : 'h-4 w-4';

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success('Link kopiert');
      setTimeout(() => setCopied(false), 1500);
    } catch (_e) {
      toast.error('Konnte nicht kopieren');
    }
  };

  return (
    <div className={`inline-flex items-center gap-2 ${className}`} data-testid="social-share-bar">
      {links.map((l) => {
        const Icon = l.icon;
        return (
          <a
            key={l.key}
            href={l.href}
            target="_blank"
            rel="noopener noreferrer"
            title={`Auf ${l.label} teilen`}
            data-testid={`share-${l.key}`}
            className={`${dims} rounded-full flex items-center justify-center text-white transition-transform hover:scale-110 hover:shadow-lg`}
            style={{ backgroundColor: l.color }}
          >
            <Icon className={iconDims} />
          </a>
        );
      })}
      <button
        type="button"
        onClick={copy}
        title="Link kopieren"
        data-testid="share-copy"
        className={`${dims} rounded-full flex items-center justify-center bg-[#1a1a1a] border border-[#C4A55A]/40 text-[#C4A55A] hover:bg-[#C4A55A]/15 transition-all`}
      >
        {copied ? <Check className={iconDims} /> : <Link2 className={iconDims} />}
      </button>
    </div>
  );
};
